import React, { useState } from 'react';
import { formatDateTime, getStatusColor, getSubStatusColor } from '../utils/helpers';
import { updateEmployeeScratchOrder, splitEmployeeScratchOrder, getOrderById } from '../services/api';
import SplitModal from '../components/common/SplitModal';
import EditScratchOrdersModal from '../components/common/EditScratchOrdersModal';
import { useAuth } from '../context/AuthContext';

const formatNumber = (num) => Number(num).toLocaleString('en-US');

const warehouseMap = {
    EBL_WH: 'Erbil Warehouse',
    DUH_WH: 'Dohuk Warehouse',
    SUL_WH: 'Suly Warehouse',
    KRK_WH: 'Kirkuk Warehouse',
    MSL_WH: 'Mosul Warehouse',
    BGD_WH: 'Baghdad Warehouse',
};

const EmployeeOrderDetailsView = ({ order, onOrderUpdate }) => {
    const { user } = useAuth();
    const isEmployee = user?.role === 'KTE-Employees' || user?.accountType === 'KTE-Employees';

    const [updatingScratchOrders, setUpdatingScratchOrders] = useState(false);
    const [scratchOrdersError, setScratchOrdersError] = useState('');
    const [scratchOrdersSuccess, setScratchOrdersSuccess] = useState('');
    const [refreshingOrder, setRefreshingOrder] = useState(false);
    const [selectedScratchOrderForEdit, setSelectedScratchOrderForEdit] = useState(null);

    // Split modal state
    const [showSplitModal, setShowSplitModal] = useState(false);
    const [selectedScratchOrderForSplit, setSelectedScratchOrderForSplit] = useState(null);
    const [splittingOrder, setSplittingOrder] = useState(false);

    const handleSplitScratchOrder = (scratchOrder) => {
        setSelectedScratchOrderForSplit(scratchOrder);
        setShowSplitModal(true);
    };

    const handleCloseSplitModal = () => {
        setShowSplitModal(false);
        setSelectedScratchOrderForSplit(null);
    };

    const handleSplitOrder = async (orderLineId, splits) => {
        setSplittingOrder(true);
        setScratchOrdersError('');
        try {
            await splitEmployeeScratchOrder(orderLineId, splits);
            setScratchOrdersSuccess('Order split successfully!');
            await refreshOrderData();
            setShowSplitModal(false);
            setTimeout(() => setScratchOrdersSuccess(''), 3000);
        } catch (error) {
            setScratchOrdersError(`Failed to split: ${error.message}`);
        } finally {
            setSplittingOrder(false);
        }
    };

    const refreshOrderData = async () => {
        if (!order?.id) return;
        setRefreshingOrder(true);
        try {
            const refreshedOrder = await getOrderById(order.id);
            if (onOrderUpdate) {
                onOrderUpdate(order.id, null, { refresh: true, order: refreshedOrder });
            }
        } catch (error) {
            setScratchOrdersError('Failed to refresh data.');
        } finally {
            setRefreshingOrder(false);
        }
    };

    const handleSaveScratchOrder = async (id, itemEdits) => {
        setUpdatingScratchOrders(true);
        setScratchOrdersError('');
        try {
            const original = order.scratchCardOrders.find(item => String(item.id) === String(id));
            if (!original) {
                console.error(`Scratch card order with ID ${id} not found in order.scratchCardOrders`);
                setScratchOrdersError(`Error: Scratch order details not found.`);
                setSelectedScratchOrderForEdit(null);
                return;
            }

            const originalFrom = original.from !== undefined && original.from !== null ? String(original.from).trim() : '';
            const originalTo = original.to !== undefined && original.to !== null ? String(original.to).trim() : '';
            const originalSubStatus = original.subStatus !== undefined && original.subStatus !== null ? String(original.subStatus).trim() : '';

            const editFrom = itemEdits.from !== undefined && itemEdits.from !== null ? String(itemEdits.from).trim() : '';
            const editTo = itemEdits.to !== undefined && itemEdits.to !== null ? String(itemEdits.to).trim() : '';
            const editSubStatus = itemEdits.subStatus !== undefined && itemEdits.subStatus !== null ? String(itemEdits.subStatus).trim() : '';

            const hasChanges = (
                editFrom !== originalFrom ||
                editTo !== originalTo ||
                (!isEmployee && editSubStatus !== originalSubStatus)
            );

            if (hasChanges) {
                const updateData = {};
                
                if (!isEmployee) {
                    updateData.subStatus = editSubStatus || null;
                }
                
                updateData.from = editFrom || null;
                updateData.to = editTo || null;

                const parsedId = isNaN(parseInt(id)) ? id : parseInt(id);
                await updateEmployeeScratchOrder(parsedId, updateData);
                setScratchOrdersSuccess('Updated successfully!');
                
                await refreshOrderData();
                
                setTimeout(() => {
                    setScratchOrdersSuccess('');
                }, 3000);
            }
            setSelectedScratchOrderForEdit(null);
        } catch (error) {
            setScratchOrdersError(`Failed to update: ${error.message}`);
        } finally {
            setUpdatingScratchOrders(false);
        }
    };

    return (
        <div className="bg-white p-4 sm:p-8 rounded-lg shadow-sm border border-gray-200 max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Order Details</h2>
            <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><span className="font-semibold">Order ID:</span> {order.id}</div>
                <div>
                    <span className="font-semibold">Status:</span> <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                </div>
                <div><span className="font-semibold">Dealer:</span> {order.assignedDealer || order.dealerName || '-'}</div>
                <div><span className="font-semibold">Created:</span> {formatDateTime(order.createdAt)}</div>
            </div>

            {order.scratchCardOrders && order.scratchCardOrders.length > 0 && (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4">Scratch Card Orders</h3>
                    
                    {scratchOrdersSuccess && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
                            {scratchOrdersSuccess}
                        </div>
                    )}
                    {scratchOrdersError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                            {scratchOrdersError}
                        </div>
                    )}

                    <div className="overflow-x-auto w-full border border-gray-200 rounded-lg mb-2 shadow-sm">
                        <table className="w-full min-w-[800px] text-sm divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Warehouse</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Denomination</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Quantity</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">From</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">To</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Sub Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {order.scratchCardOrders.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{warehouseMap[item.warehouse] || item.warehouse}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900 font-medium tabular-nums">IQD {formatNumber(item.denomination)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.quantity}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.from || '-'}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">{item.to || '-'}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 tabular-nums">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSubStatusColor(item.subStatus)}`}>
                                                {item.subStatus || '-'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap flex items-center gap-2">
                                            <button
                                                onClick={() => handleSplitScratchOrder(item)}
                                                disabled={splittingOrder}
                                                className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                                            >
                                                Split
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setScratchOrdersError('');
                                                    setSelectedScratchOrderForEdit(item);
                                                }}
                                                disabled={refreshingOrder}
                                                className="px-3 py-1.5 bg-amber-500 text-white rounded text-xs font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {refreshingOrder && (
                        <div className="mt-4 flex gap-4">
                            <span className="text-sm text-gray-500 animate-pulse flex items-center">Refreshing...</span>
                        </div>
                    )}
                </div>
            )}

            <SplitModal
                isOpen={showSplitModal}
                onClose={handleCloseSplitModal}
                scratchOrder={selectedScratchOrderForSplit}
                onSplit={handleSplitOrder}
            />

            <EditScratchOrdersModal
                isOpen={selectedScratchOrderForEdit !== null}
                onClose={() => setSelectedScratchOrderForEdit(null)}
                scratchCardOrder={selectedScratchOrderForEdit}
                isEmployee={isEmployee}
                onSave={handleSaveScratchOrder}
                isSaving={updatingScratchOrders}
                error={scratchOrdersError}
            />
        </div>
    );
};

export default EmployeeOrderDetailsView;
